/**
 * Global tenant guard (ADR-0036 item #6). Before rendering ANY route, it asks the
 * backend whether THIS host maps to a real tenant. On an unknown tenant subdomain
 * it shows a 404 instead of a misleading login (which would silently land on the
 * default `public` tenant). The apex / bare localhost resolves to `default` →
 * everything renders as normal, so this is a no-op for the single-tenant setup.
 *
 * PROVISIONING PATIENCE (ADR-0074): a fresh signup/checkout redirect lands here
 * with `?provisioning=1` BEFORE the cell has consumed the provisioning saga (at
 * the split, Control's "completed" precedes the cell's schema by a few relay
 * ticks) — so with that flag an `unknown` probe is NOT a 404: we show a branded
 * "preparing your workspace" wait and re-poll until the tenant resolves (or a
 * generous timeout falls back to the 404). Once resolved the flag is stripped so
 * a later refresh never re-arms the patience.
 */
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { TenantNotFound } from "@/pages/TenantNotFound";
import { TenantSuspended } from "@/pages/TenantSuspended";
import { fetchTenantContext } from "@/lib/platform";

const PROVISIONING_POLL_MS = 2500;
const PROVISIONING_MAX_MS = 120_000;

function provisioningFlag(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("provisioning") === "1";
}

function ProvisioningWait() {
  const { t } = useTranslation();
  return (
    <div className="grid min-h-screen place-items-center bg-background p-4">
      <div className="w-full max-w-md rounded-xl border border-border/60 bg-surface p-8 text-center shadow-lg">
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <img src="/tray-logo.svg" alt="" aria-hidden="true" className="h-7 w-7 shrink-0" />
          <span className="text-base font-semibold tracking-tight">Tray</span>
        </div>
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
        <h1 className="mt-4 text-lg font-semibold text-foreground">
          {t("onboarding.provisioning.title", "Stiamo creando il tuo workspace…")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t(
            "onboarding.provisioning.subtitle",
            "Ci vuole solo qualche secondo. Non chiudere questa pagina.",
          )}
        </p>
      </div>
    </div>
  );
}

export function TenantGate({ children }: { children: React.ReactNode }) {
  // Captured ONCE at mount: mid-session query-string changes must not re-arm it.
  const [provisioning] = React.useState(provisioningFlag);
  const armedAt = React.useRef(Date.now());
  const [expired, setExpired] = React.useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["tenant-context"],
    queryFn: fetchTenantContext,
    staleTime: Infinity,
    retry: false,
    // While armed and still unknown, keep asking — the cell consumes the saga in
    // a few relay ticks (refetchInterval fires regardless of staleTime).
    refetchInterval: (query) => {
      if (!provisioning || expired) return false;
      const status = query.state.data?.status;
      if (status && status !== "unknown") return false;
      return PROVISIONING_POLL_MS;
    },
  });

  // The poll's refetchInterval just STOPS on timeout — this re-render is what
  // actually swaps the wait screen for the honest 404.
  React.useEffect(() => {
    if (!provisioning) return;
    const timer = window.setTimeout(
      () => setExpired(true),
      Math.max(0, PROVISIONING_MAX_MS - (Date.now() - armedAt.current)),
    );
    return () => window.clearTimeout(timer);
  }, [provisioning]);

  // Once the tenant resolves, drop the flag so a later refresh renders normally.
  React.useEffect(() => {
    if (!provisioning || !data || data.status === "unknown") return;
    const url = new URL(window.location.href);
    url.searchParams.delete("provisioning");
    window.history.replaceState({}, "", url);
  }, [provisioning, data]);

  // While the (fast, server-cached) probe is in flight, render nothing rather
  // than flash the login on what might be an unknown tenant.
  if (isLoading) return null;
  if (data?.status === "unknown") {
    if (provisioning && !expired) return <ProvisioningWait />;
    return <TenantNotFound slug={data.slug} />;
  }
  if (data?.status === "suspended") return <TenantSuspended slug={data.slug} />;
  return <>{children}</>;
}
